import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { followUser, getPublicProfile, unfollowUser } from '../../api/client';
import type { PublicProfileApi } from '../../api/client';

export default function PublicProfile() {
  const navigate = useNavigate();
  const { username = '' } = useParams();
  const [profile, setProfile] = useState<PublicProfileApi | null>(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    if (!username) return;
    setLoading(true);
    try {
      const data = await getPublicProfile(username);
      setProfile(data);
    } catch {
      setProfile(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [username]);

  const toggleFollow = async () => {
    if (!profile) return;
    try {
      if (profile.is_following) await unfollowUser(profile.id);
      else await followUser(profile.id);
      await load();
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-orange-50 to-red-50">
      <div className="fixed top-14 left-0 right-0 bg-white/95 backdrop-blur-md border-b border-orange-100 z-50">
        <div className="px-4 py-4 flex items-center justify-between">
          <button onClick={() => navigate('/')} className="w-8 h-8 flex items-center justify-center bg-orange-100 rounded-full">
            <i className="ri-arrow-left-line text-orange-600 text-sm" />
          </button>
          <h1 className="text-lg font-semibold text-gray-800">Profile</h1>
          <div />
        </div>
      </div>

      <div className="pt-32 pb-8 px-4">
        {loading ? (
          <p className="text-center text-gray-500 py-10">Loading profile...</p>
        ) : !profile ? (
          <p className="text-center text-gray-500 py-10">Profile not found.</p>
        ) : (
          <div className="space-y-6">
            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <div className="flex items-center gap-4">
                {profile.avatar_url ? (
                  <img src={profile.avatar_url} alt={profile.display_name} className="w-20 h-20 rounded-full object-cover" />
                ) : (
                  <div className="w-20 h-20 rounded-full bg-stone-100 flex items-center justify-center">
                    <i className="ri-user-line text-2xl text-stone-500" />
                  </div>
                )}
                <div className="flex-1">
                  <h2 className="text-xl font-bold text-gray-800">{profile.display_name}</h2>
                  <p className="text-sm text-gray-600">@{profile.username}</p>
                  <p className="text-xs text-gray-500 mt-1">{profile.followers_count} followers • {profile.following_count} following</p>
                </div>
                <button onClick={toggleFollow} className={`px-4 py-2 rounded-lg text-sm font-medium ${profile.is_following ? 'bg-gray-100 text-gray-700' : 'bg-orange-600 text-white'}`}>
                  {profile.is_following ? 'Following' : 'Follow'}
                </button>
              </div>
              {profile.bio ? <p className="text-sm text-gray-700 mt-4">{profile.bio}</p> : null}
            </div>

            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-gray-800 mb-3">Playlists</h3>
              {profile.playlists.length === 0 ? (
                <p className="text-sm text-gray-500">No public playlists yet.</p>
              ) : (
                <div className="space-y-3">
                  {profile.playlists.map((pl) => (
                    <Link
                      key={pl.id}
                      to={`/playlist/${pl.id}`}
                      className="block rounded-xl border border-transparent bg-gray-50 p-3 transition hover:border-orange-200 hover:bg-white"
                    >
                      <p className="font-medium text-sm text-gray-800">{pl.name}</p>
                      <p className="text-xs text-gray-600 line-clamp-2">{pl.description || 'No description'}</p>
                      <p className="text-xs text-gray-500 mt-1">{pl.track_count} tracks • {pl.likes_count} likes</p>
                      <p className="text-xs font-medium text-orange-700 mt-2">Open playlist →</p>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

